/**
 * Hash routing.
 *
 * Hash rather than the History API so the app works when opened from a file,
 * from an installed home-screen shortcut, or from a static host with no rewrite
 * rules, without any of them needing to agree on server config.
 */

type Screen = HTMLElement | Promise<HTMLElement>
type Route = (params: string[]) => Screen

const routes: { pattern: RegExp; handler: Route }[] = []
let host: HTMLElement | null = null

export function route(pattern: RegExp, handler: Route): void {
  routes.push({ pattern, handler })
}

export function navigate(path: string): void {
  const target = `#${path}`
  if (window.location.hash === target) void render()
  else window.location.hash = target
}

export function startRouter(mount: HTMLElement): void {
  host = mount
  window.addEventListener('hashchange', () => void render())
  void render()
}

async function render(): Promise<void> {
  if (!host) return
  const path = window.location.hash.replace(/^#/, '') || '/'

  for (const { pattern, handler } of routes) {
    const match = pattern.exec(path)
    if (!match) continue
    const params = match.slice(1).map((p) => decodeURIComponent(p ?? ''))
    try {
      const screen = await handler(params)
      host.replaceChildren(screen)
      // Reset scroll and move focus to the new heading, or a route change is
      // silent to a screen reader and lands mid-page for everyone else.
      window.scrollTo(0, 0)
      const title = host.querySelector<HTMLElement>('.app-title')
      if (title) {
        title.setAttribute('tabindex', '-1')
        title.focus({ preventScroll: true })
      }
    } catch (e) {
      host.replaceChildren(errorScreen(e as Error))
    }
    return
  }
  navigate('/')
}

function errorScreen(error: Error): HTMLElement {
  const div = document.createElement('div')
  div.className = 'screen'
  div.innerHTML = '<h1 class="app-title">Something went wrong</h1>'
  const message = document.createElement('p')
  message.className = 'warn'
  message.textContent = error.message
  div.append(message)
  const back = document.createElement('button')
  back.className = 'btn btn-primary'
  back.textContent = 'Back to start'
  back.addEventListener('click', () => navigate('/'))
  div.append(back)
  return div
}
